import React from 'react';
import {Flex, ToolbarIcons} from '@priority-software/priority-style-react';
import styles from './styles.module.scss';
import {EmojiPicker} from '../../../../../Form/components/HTMLFormEditor/Editor/plugins/ToolbarPlugin/EmojiPicker';
import {FontDropdown} from './plugins/FontDropdown';
import {FontDropDownMode} from '../../../../../Form/components/HTMLFormEditor/Editor/plugins/ToolbarPlugin/Font-dropdown/enums';
import {FONT_FAMILY_OPTIONS, FONT_SIZE_OPTIONS} from '../../constants';
import ColorPicker from '../../../../../../features/Form/components/HTMLFormEditor/Editor/ui/ColorPicker/ColorPicker';
import {DEFAULT_COLORS_MAP} from '../../../../../Form/components/HTMLFormEditor/Editor/constants';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {FORMAT_ELEMENT_COMMAND, FORMAT_TEXT_COMMAND} from 'lexical';
import cx from 'classnames';
import {useCustomDividerCommand} from '../hooks/useCustomDividerCommand';
import {useCustomClipboardCommands} from '../hooks/useCustomClipboardCommands';
import {useCustomFontColorCommand} from '../hooks/useCustomFontColorCommand';
import {useCustomFontFormatCommand} from '../hooks/useCustomFontFormatCommand';
import {useCustomToolbarPlugin} from '../hooks/useCustomToolbarPlugin';
import {emailDialogID} from '../../../../testIDs';
import {useFormatList} from '../hooks/useFormatList';
import {useFormatIndent} from '../hooks/useFormatIndent';
import {createDispatchCommand} from './utils';
import {EFormatElement, EFormatIncreaseDecrease, EFormatText} from './enums';
import {IconWithPopover} from '../../../IconWithPopover';
import {texts} from '../../../../texts';
import {useEmailContext} from '../../../../context';

const Divider = () => <div className={styles.divider} />;

export const ToolbarPlugin = ({setIsDropdownOpen}) => {
    const {states} = useEmailContext();

    const [editor] = useLexicalComposerContext();

    const dispatchCommand = createDispatchCommand(editor);

    const {handlePaste, handleCopyAndCut} = useCustomClipboardCommands({editor});
    const {lastSelectedColor, onFontColorSelect} = useCustomFontColorCommand({editor});
    const {fontFormat, handleFontSizeChange, setFontFormat} = useCustomFontFormatCommand({editor});
    const {handleDividerInsert} = useCustomDividerCommand({editor});
    const {formatBulletList, formatNumberedList, setBlockType} = useFormatList({editor});
    const {formatIndent} = useFormatIndent({editor});
    useCustomToolbarPlugin({editor, setFontFormat, setBlockType});

    return (
        <Flex wrap justifyContent="flex-start">
            {/* Font family & size */}
            <FontDropdown
                options={FONT_FAMILY_OPTIONS}
                mode={FontDropDownMode.fontFamily}
                value={{label: 'Arial', value: 'Arial'}}
                editor={editor}
                setIsDropdownOpen={setIsDropdownOpen}
            />
            <FontDropdown
                options={FONT_SIZE_OPTIONS}
                mode={FontDropDownMode.fontSize}
                value={{label: '12', value: '12'}}
                editor={editor}
                setIsDropdownOpen={setIsDropdownOpen}
            />
            <IconWithPopover
                type="icon"
                content={texts.reduce}
                icon={ToolbarIcons.toolbarTextDecrease}
                onClick={() => handleFontSizeChange(EFormatIncreaseDecrease.DECREASE)}
            />
            <IconWithPopover
                type="icon"
                content={texts.enlarge}
                icon={ToolbarIcons.toolbarTextIncrease}
                onClick={() => handleFontSizeChange(EFormatIncreaseDecrease.INCREASE)}
            />
            <Divider />
            {/* Color, copy & paste */}
            <ColorPicker
                colorsPalette={DEFAULT_COLORS_MAP}
                buttonClassName="priority-editor__toolbar-item color-picker"
                buttonAriaLabel="Formatting text color"
                color={lastSelectedColor.current}
                onChange={onFontColorSelect}
                title="text color"
                content={texts.color_picker}
                isWithoutPlate
            />
            <IconWithPopover
                type="icon"
                content={texts.cut}
                icon={ToolbarIcons.toolbarCut}
                onClick={() => handleCopyAndCut(true)}
            />
            <IconWithPopover
                type="icon"
                content={texts.copy}
                icon={ToolbarIcons.toolbarCopy}
                onClick={() => handleCopyAndCut()}
            />
            <IconWithPopover
                type="icon"
                content={texts.paste}
                icon={ToolbarIcons.toolbarPaste}
                onClick={handlePaste}
            />
            <Divider />
            {/* Bold, italic, underline, strikethrough */}
            <IconWithPopover
                type="icon"
                content={texts.bold}
                componentClasses={cx('priority-editor__toolbar-item', {active: fontFormat.isBold})}
                onClick={() => dispatchCommand<EFormatText>(FORMAT_TEXT_COMMAND, EFormatText.BOLD)}
                icon={ToolbarIcons.toolbarBold}
            />
            <IconWithPopover
                type="icon"
                content={texts.italic}
                componentClasses={cx('priority-editor__toolbar-item', {
                    active: fontFormat.isItalic
                })}
                onClick={() =>
                    dispatchCommand<EFormatText>(FORMAT_TEXT_COMMAND, EFormatText.ITALIC)
                }
                icon={ToolbarIcons.toolbarItalic}
            />
            <IconWithPopover
                type="icon"
                content={texts.underline}
                componentClasses={cx('priority-editor__toolbar-item', {
                    active: fontFormat.isUnderline
                })}
                onClick={() =>
                    dispatchCommand<EFormatText>(FORMAT_TEXT_COMMAND, EFormatText.UNDERLINE)
                }
                icon={ToolbarIcons.toolbarUnderline}
            />
            <IconWithPopover
                type="icon"
                content={texts.strikethrough}
                componentClasses={cx('priority-editor__toolbar-item', {
                    active: fontFormat.isStrikethrough
                })}
                onClick={() =>
                    dispatchCommand<EFormatText>(FORMAT_TEXT_COMMAND, EFormatText.STRIKETHROUGH)
                }
                icon={ToolbarIcons.toolbarFormatStrikethrough}
            />

            <Divider />

            {/* Align left, center, right, indent */}
            <IconWithPopover
                type="icon"
                content={texts.align_left}
                onClick={() =>
                    dispatchCommand<EFormatElement>(FORMAT_ELEMENT_COMMAND, EFormatElement.LEFT)
                }
                icon={ToolbarIcons.toolbarFormatAlignLeft}
            />
            <IconWithPopover
                type="icon"
                content={texts.align_center}
                onClick={() =>
                    dispatchCommand<EFormatElement>(FORMAT_ELEMENT_COMMAND, EFormatElement.CENTER)
                }
                icon={ToolbarIcons.toolbarFormatAlignCenter}
            />
            <IconWithPopover
                type="icon"
                content={texts.align_right}
                onClick={() =>
                    dispatchCommand<EFormatElement>(FORMAT_ELEMENT_COMMAND, EFormatElement.RIGHT)
                }
                icon={ToolbarIcons.toolbarFormatAlignRight}
            />
            <IconWithPopover
                type="icon"
                content={texts.right_indentation}
                icon={ToolbarIcons.toolbarFormatIndent_increase}
                onClick={() => formatIndent(EFormatIncreaseDecrease.INCREASE)}
            />
            <IconWithPopover
                type="icon"
                content={texts.left_indentation}
                icon={ToolbarIcons.toolbarFormatIndent_decrease}
                onClick={() => formatIndent(EFormatIncreaseDecrease.DECREASE)}
            />
            <Divider />
            {/* List, image */}
            <IconWithPopover
                type="icon"
                content={texts.bulleted_list}
                icon={ToolbarIcons.toolbarFormatListBulleted}
                onClick={formatBulletList}
            />
            <IconWithPopover
                type="icon"
                content={texts.numbered_list}
                icon={ToolbarIcons.toolbarFormatListNumbered}
                onClick={formatNumberedList}
            />
            <Divider />
            {/* Horizontal rule, emoji */}
            <IconWithPopover
                type="icon"
                content={texts.divider}
                icon={ToolbarIcons.toolbarHorizontalRule}
                onClick={handleDividerInsert}
            />
            <EmojiPicker
                portalSelector={emailDialogID}
                content={texts.emoji}
                disabled={states.focusSubject}
            />
        </Flex>
    );
};
